import { combineReducers } from 'redux';
import * as signUpActions from 'stash/actions/signUp';
import {
	GET_ENTITLEMENTS_SUCCESS,
	BOOTSTRAP_GET_CART_ITEMS_SUCCESS,
	SET_MULTI_AUTOSTASH,
} from 'stash/actions';
import {
	hasEntitlement,
	type EntitlementRecord,
	OPEN_ACCOUNT_PERSONAL_ROBO,
	OPEN_ACCOUNT_PERSONAL_SELF,
	VIEW_CONTENT_REPORT_PREMIUM,
	OPEN_ACCOUNT_BANKING,
	INVEST_STOCKS_AND_ETFS,
} from 'stash/utils/entitlements';
import multiFunding from './multiFunding';

type SignUpAction = {
	type: string;
	name?: string;
	value?: unknown;
	data?: Record<string, unknown>;
	response?: {
		entitlements?: EntitlementRecord[];
		cart_items_with_cards?: Array<Record<string, unknown>>;
	};
};

export const initCompletedState: Record<string, unknown> = {};

export const completed = (
	state: Record<string, unknown> = initCompletedState,
	action: SignUpAction
): Record<string, unknown> => {
	switch (action.type) {
		case signUpActions.SET_PROGRESS_STEP:
		case signUpActions.SET_EMPLOYED:
		case signUpActions.SET_AUTO_INVEST_MIX:
		case signUpActions.SKIP_TO_LINK_BANK_PRIMER:
		case signUpActions.SET_LINK_BANK_PRIMER_COMPLETE:
		case signUpActions.SET_LINK_BANK_LATER:
		case signUpActions.SET_SKIP_BANK_LINK:
		case signUpActions.SET_COMPLETED_REFERRAL:
		case signUpActions.SET_PORTFOLIO_BUILDER_HANDOFF_COMPLETE:
		case signUpActions.SET_STASHWORKS_BENEFIT_LINKED:
		case signUpActions.SET_BILLING_SUMMARY:
		case signUpActions.SET_BILLING_FREQUENCY:
		case signUpActions.SET_ADD_TRUSTED_CONTACT:
		case signUpActions.SET_WELCOME_TO_STASH_COMPLETE:
		case signUpActions.SET_BANK_LINK_OAUTH:
		case signUpActions.SET_SCHEDULE_COMPLETE:
		case signUpActions.SET_FIRST_DEPOSIT_COMPLETE:
		case signUpActions.SET_SMART_PORTFOLIO_TRANSITION_COMPLETE:
		case signUpActions.SET_SMART_PORTFOLIO_V2_TRANSITION_COMPLETE:
		case signUpActions.SET_SMART_PORTFOLIO_ALLOCATION_COMPLETE:
		case signUpActions.SET_INVESTMENT_SELECTION_COMPLETE:
		case signUpActions.SET_INVESTMENT_DETAIL_COMPLETE:
		case signUpActions.SET_ADD_CASH:
		case signUpActions.SET_ADD_CASH_AMOUNT_COMPLETE:
		case signUpActions.SET_AUTO_INVEST_ADD_CASH_SUMMARY_COMPLETE:
		case signUpActions.SET_ADD_CASH_SUCCESS_COMPLETE:
		case signUpActions.SET_DUPLICATE_SSN_BLOCK_SCREEN:
		case signUpActions.SET_VALID_SSN_COMPLETE:
		case signUpActions.SET_STATE_ZERO_HOME_COMPLETE:
		case signUpActions.SET_RETIRE_STATE_ZERO_COMPLETE:
		case signUpActions.SET_RETIRE_QUESTIONS_PRIMER_COMPLETE:
		case signUpActions.SET_RETIRE_MARITAL_STATUS_COMPLETE:
		case signUpActions.SET_RETIRE_TAX_FILING_STATUS_COMPLETE:
		case signUpActions.SET_RETIRE_COMBINED_ANNUAL_INCOME_COMPLETE:
		case signUpActions.SET_RETIRE_WORKPLACE_RETIREMENT_PLAN_COMPLETE:
		case signUpActions.SET_RETIRE_RECOMMENDATION_TRANSITION_COMPLETE:
		case signUpActions.SET_RETIRE_RECOMMENDATION_READY_COMPLETE:
		case signUpActions.SET_RETIRE_IRA_TYPE_SELECTION_COMPLETE:
		case signUpActions.SET_RETIRE_IRA_TYPE_SELECTION_ENTRY_FROM_BYO:
		case signUpActions.SET_RETIRE_RECOMMENDATION_CONTINUE_COMPLETE:
		case signUpActions.SET_RETIRE_MANAGED_ACCOUNT_OPEN_TRANSITION_COMPLETE:
		case signUpActions.SET_IDV_CHECK_LOADER_COMPLETE:
		case signUpActions.SET_IDV_SUCCESS_COMPLETE:
		case signUpActions.SET_SMART_SUCCESS_COMPLETE:
		case signUpActions.SET_ONBOARDING_COMPLETE:
		case signUpActions.SET_PLAN_SELECTION_COMPLETE:
		case signUpActions.SET_STOCKPILE_STEP_CONFIRMED:
		case signUpActions.SET_ONBOARDING_FUNDING_SKIPPED:
		case signUpActions.SET_MANAGED_RETIRE_FUNDING_LINK_BANK:
		case signUpActions.SET_MANAGED_RETIRE_FUNDING_CADENCE_COMPLETE:
			return {
				...state,
				[action.name as string]: action.value,
			};

		default:
			return state;
	}
};

export const initDataState: Record<string, unknown> = {};

const data = (
	state: Record<string, unknown> = initDataState,
	action: SignUpAction
): Record<string, unknown> => {
	switch (action.type) {
		case signUpActions.SET_SIGN_UP_ACCOUNT_TYPE:
			return {
				...state,
				...action.data,
			};
		case SET_MULTI_AUTOSTASH:
			return {
				...state,
				auto_stash: action.data,
			};
		default:
			return state;
	}
};

export const initFirstPurchaseState: Record<string, unknown> = {};

export const firstPurchase = (
	state: Record<string, unknown> = initFirstPurchaseState,
	action: SignUpAction
): Record<string, unknown> => {
	switch (action.type) {
		case BOOTSTRAP_GET_CART_ITEMS_SUCCESS: {
			const cartItems = action.response?.cart_items_with_cards || [{}];
			const cartItem = cartItems[0];

			return {
				...state,
				...cartItem,
			};
		}

		default:
			return state;
	}
};

const initialEntitlementFlagsState: Record<string, boolean> = {};

const entitlementFlags = (
	state: Record<string, boolean> = initialEntitlementFlagsState,
	action: SignUpAction
): Record<string, boolean> => {
	switch (action.type) {
		case GET_ENTITLEMENTS_SUCCESS:
			return {
				...state,
				hasSmartEntitlement: hasEntitlement(
					action.response?.entitlements ?? [],
					OPEN_ACCOUNT_PERSONAL_ROBO
				),
				hasPersonalEnititlement: hasEntitlement(
					action.response?.entitlements ?? [],
					OPEN_ACCOUNT_PERSONAL_SELF
				),
				hasPremiumMarketReportEntitlement: hasEntitlement(
					action.response?.entitlements ?? [],
					VIEW_CONTENT_REPORT_PREMIUM
				),
				hasBankEntitlement: hasEntitlement(
					action.response?.entitlements ?? [],
					OPEN_ACCOUNT_BANKING
				),
				hasInvestStocksAndEtfsEntitlement: hasEntitlement(
					action.response?.entitlements ?? [],
					INVEST_STOCKS_AND_ETFS
				),
			};
		default:
			return state;
	}
};

export default combineReducers({
	completed,
	data,
	entitlementFlags,
	firstPurchase,
	multiFunding,
});
